import { AnimatePresence, motion } from 'framer-motion';
import { Check, ChevronDown, LogOut, Settings } from 'lucide-react';
import { useState } from 'react';
import tokenSkrIcon from '../../assets/skr-logo.svg';
import { cn } from '../../lib/utils';
import type { RightResult, RouterOutputs } from '../../types/TRPC';
import { LogoFull } from '../LogoFull';
import { EASE_OUT, InitialsAvatar } from '../portal';
import { NavRow } from './NavRow';

declare global {
  interface Window {
    OneTrust?: {
      ToggleInfoDisplay?: () => void;
    };
  }
}

type PublisherSummary = RightResult<RouterOutputs['getActivePublisher']>;

export interface PublisherMenuProps {
  name: string;
  avatarUrl: string | null;
  role: string | null;
  publisherType: 'Individual' | 'Organization' | null;
  accessiblePublishers: PublisherSummary[];
  activePublisherUserJoinId?: string | null;
  isSwitchingPublisher?: boolean;
  onSwitchPublisher: (publisherUserJoinId: string) => void;
  onSettings: () => void;
  onSignOut: () => void;
  showSkrClaim?: boolean;
  onSkrClaim?: () => void;
}

const LEGAL_LINKS = [
  {
    label: 'Terms of Service',
    href: 'https://docs.solanamobile.com/dapp-store/agreement',
  },
  {
    label: 'Privacy Policy',
    href: 'https://docs.solanamobile.com/dapp-store/privacy-policy',
  },
  {
    label: 'Cookie Policy',
    href: 'https://solanamobile.com/cookie-policy-web',
  },
];

const LEGAL_LINK_CLASSES =
  'rounded-sm transition-colors duration-150 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70';

/** Figma "Menu" popover opened from the publisher card. */
export function PublisherMenu({
  name,
  avatarUrl,
  role,
  publisherType,
  accessiblePublishers,
  activePublisherUserJoinId,
  isSwitchingPublisher = false,
  onSwitchPublisher,
  onSettings,
  onSignOut,
  showSkrClaim = false,
  onSkrClaim,
}: PublisherMenuProps) {
  const [isLegalOpen, setIsLegalOpen] = useState(false);
  const publisherTypeLabel = publisherType;

  return (
    <div
      aria-label="Publisher menu"
      className="flex flex-col overflow-hidden rounded-lg border border-seeker-800 bg-seeker-900 shadow-[0_0_20px_rgba(0,0,0,0.25)]"
    >
      <div className="flex h-16 items-center gap-3 bg-black px-4">
        <InitialsAvatar name={name} imageUrl={avatarUrl} size={32} />
        <div className="flex min-w-0 flex-1 flex-col">
          <span
            title={name}
            className="truncate text-sm font-bold leading-5 text-white"
          >
            {name}
          </span>
          {(role || publisherTypeLabel) && (
            <span className="flex gap-2 truncate text-xs leading-4 text-seeker-400">
              {role && <span>{role}</span>}
              {publisherTypeLabel && <span>{publisherTypeLabel}</span>}
            </span>
          )}
        </div>
      </div>

      {accessiblePublishers.length > 1 && (
        <div className="flex flex-col gap-1 p-2 pt-4">
          <div className="px-2 pb-1 text-xs font-semibold uppercase leading-4 tracking-[0.01em] text-white/70">
            Your Companies
          </div>
          <ul className="flex max-h-56 flex-col gap-1 overflow-y-auto">
            {accessiblePublishers.map((publisher) => {
              const isActive =
                publisher.publisherUserJoinId === activePublisherUserJoinId;
              return (
                <li key={publisher.publisherUserJoinId}>
                  <button
                    type="button"
                    disabled={isSwitchingPublisher}
                    aria-current={isActive ? 'true' : undefined}
                    onClick={() =>
                      onSwitchPublisher(publisher.publisherUserJoinId)
                    }
                    className={cn(
                      'flex h-16 w-full items-center gap-3 rounded-lg px-4 text-left transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-white/70',
                      isActive
                        ? 'bg-black text-white'
                        : 'bg-seeker-800 text-white hover:bg-seeker-700/70',
                      isSwitchingPublisher && 'cursor-not-allowed opacity-50',
                    )}
                  >
                    <InitialsAvatar
                      name={publisher.name}
                      imageUrl={
                        publisher.avatarThumbnailUrl || publisher.avatarUrl
                      }
                      size={32}
                    />
                    <span className="flex min-w-0 flex-1 flex-col">
                      <span
                        title={publisher.name}
                        className="truncate text-sm font-bold leading-5"
                      >
                        {publisher.name}
                      </span>
                      <span className="truncate text-xs leading-4 text-seeker-400">
                        {publisher.role}
                      </span>
                    </span>
                    {isActive && <Check className="size-4 shrink-0" />}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <div className="flex flex-col gap-1 p-2">
        {showSkrClaim && (
          <NavRow
            icon={
              <img
                src={tokenSkrIcon}
                alt=""
                aria-hidden="true"
                className="size-4 rounded-full"
              />
            }
            label="Claim Your SKR"
            onClick={onSkrClaim}
            className="[&>span]:bg-[linear-gradient(90deg,#96A3A3_0%,#96A3A3_40%,white_50%,#96A3A3_60%,#96A3A3_100%)] [&>span]:bg-size-[200%_100%] [&>span]:bg-clip-text [&>span]:text-transparent [&>span]:animate-shimmer"
          />
        )}
        <NavRow
          icon={<Settings />}
          label="Settings"
          aria-label="Settings"
          onClick={onSettings}
        />
        <NavRow
          icon={<LogOut />}
          label="Sign Out"
          aria-label="Sign Out"
          onClick={onSignOut}
        />
      </div>

      <div className="mx-4 h-px bg-seeker-800" />

      <div className="flex flex-col px-4 py-4">
        <LogoFull type="light" className="h-3 w-auto self-start" />
        <button
          type="button"
          aria-expanded={isLegalOpen}
          onClick={() => setIsLegalOpen((open) => !open)}
          className="mt-3 flex w-full items-center justify-between rounded-sm text-seeker-500 transition-colors duration-150 hover:text-seeker-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70 focus-visible:ring-offset-2 focus-visible:ring-offset-seeker-900"
        >
          <span className="text-xs leading-4">Legal &amp; Privacy</span>
          <ChevronDown
            className={cn(
              'size-4 transition-transform duration-150 ease-out',
              isLegalOpen && 'rotate-180',
            )}
          />
        </button>
        <AnimatePresence initial={false}>
          {isLegalOpen && (
            <motion.div
              key="legal-links"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.18, ease: EASE_OUT }}
              className="-mx-1 -mb-1 overflow-hidden px-1"
            >
              <div className="flex flex-col items-start gap-2.5 pb-1 pt-3 text-xs leading-4 text-seeker-400">
                {LEGAL_LINKS.map((link) => (
                  <a
                    key={link.href}
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={LEGAL_LINK_CLASSES}
                  >
                    {link.label}
                  </a>
                ))}
                <a
                  href="https://docs.solanamobile.com/dapp-store/privacy-policy"
                  data-privacy-choices
                  onClick={(event) => {
                    if (
                      typeof window.OneTrust?.ToggleInfoDisplay === 'function'
                    ) {
                      event.preventDefault();
                      window.OneTrust.ToggleInfoDisplay();
                    }
                  }}
                  className={LEGAL_LINK_CLASSES}
                >
                  Your Privacy Choices
                </a>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
